import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import AOS from "aos";

import "./global.css";

import Navigation from "./components/Navbar";
import Footer from "./components/Footer";
import PageLoader from "./components/PageLoader";
import ScrollToTop from "./components/ScrollToTop";

import Home from "./pages/Home";

const About = lazy(() => import("./pages/About"));
const WhatWeDo = lazy(() => import("./pages/WhatWeDo"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const GEOPage = lazy(() => import("./pages/GEOPage"));
const GEOAuditPage = lazy(() => import("./pages/GEOAuditPage"));
const LearnHub = lazy(() => import("./pages/LearnHub"));
const LearnTopic = lazy(() => import("./pages/LearnTopic"));
const LearnChapter = lazy(() => import("./pages/LearnChapter"));
const People = lazy(() => import("./pages/People"));
const Insights = lazy(() => import("./pages/Insights"));
const JoinUs = lazy(() => import("./pages/JoinUs"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));

const JobDetails = lazy(() => import("./pages/job-details"));
const ApplyJob = lazy(() => import("./pages/apply-job"));

const CookieBanner = lazy(() => import("./components/CookieBanner"));

const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));

const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CheckUserType = lazy(() => import("./pages/check-user-type"));

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Welcome = lazy(() => import("./pages/Welcome"));

const DashboardEmail = lazy(() => import("./pages/DashboardEmail"));

const DashboardShell = lazy(() => import("./dashboard/DashboardShell"));

const DashboardHome = lazy(() => import("./pages/DashboardHome"));
const DashboardWorkflows = lazy(() => import("./pages/DashboardWorkflows"));
const DashboardActivity = lazy(() => import("./pages/DashboardActivity"));
const DashboardNotifications = lazy(() => import("./pages/DashboardNotifications"));
const ApplyLeave = lazy(() => import("./pages/ApplyLeave"));
const Holidays = lazy(() => import("./pages/Holidays"));
const SalarySlips = lazy(() => import("./pages/SalarySlips"));
const LeaveApproval = lazy(() => import("./pages/LeaveApproval"));
const DashboardJobs = lazy(() => import("./pages/DashboardJobs"));
const DashboardJobApplications = lazy(() => import("./pages/DashboardJobApplications"));
const DashboardSeoTasks = lazy(() => import("./pages/DashboardSeoTasks"));
const DashboardPayroll = lazy(() => import("./pages/DashboardPayroll"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const DashboardEmployeeDetail = lazy(() => import("./pages/DashboardEmployeeDetail"));
const ComingSoon = lazy(() => import("./pages/ComingSoon"));
const DashboardTaskDetail = lazy(() => import("./pages/DashboardTaskDetail"));
const DashboardTaskCreate = lazy(() => import("./pages/DashboardTaskCreate"));

const DashboardServices = lazy(() => import("./pages/DashboardServices"));
const DashboardClients = lazy(() => import("./pages/DashboardClients"));
const DashboardProjects = lazy(() => import("./pages/DashboardProjects"));
const DashboardEmployeeManagement = lazy(() => import("./pages/DashboardEmployeeManagement"));
const DashboardProjectDetail = lazy(() => import("./pages/DashboardProjectDetail"));
const DashboardServiceDetail = lazy(() => import("./pages/DashboardServiceDetail"));

const AccountTransactions = lazy(() => import("./pages/AccountTransactions"));
const AccountPayments = lazy(() => import("./pages/AccountPayments"));


// ✅ Route tracker for React SPA page views
const RouteTracker = ({ enabled }) => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: "page_view",
      page_path: location.pathname + location.search + location.hash,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [enabled, location]);

  return null;
};

// Wrapper to include Routes with smooth page transitions on public pages
const ContentWrapper = ({ enabled }) => {
  const location = useLocation();

  if (!enabled) return null;

  // Dashboard shell is persistent — skip transition to avoid re-mounting the sidebar
  const isDashboard = location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/");

  const routes = (
    <Routes location={location}>
      <Route path="/" element={<Home />} />
      <Route path="/about" element={<About />} />
      <Route path="/what-we-do" element={<WhatWeDo />} />
      <Route path="/what-we-do/geo" element={<GEOPage />} />
      <Route path="/geo-readiness-check" element={<GEOAuditPage />} />
      <Route path="/geo-readiness-check/:domain" element={<GEOAuditPage />} />
      <Route path="/what-we-do/:slug" element={<ServiceDetail />} />
      <Route path="/learn" element={<LearnHub />} />
      <Route path="/learn/:topicSlug" element={<LearnTopic />} />
      <Route path="/learn/:topicSlug/:chapterSlug" element={<LearnChapter />} />
      <Route path="/people" element={<People />} />
      <Route path="/insights" element={<Insights />} />
      <Route path="/join-us" element={<JoinUs />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="*" element={<NotFound />} />

      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-use" element={<TermsOfUse />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/disclaimer" element={<Disclaimer />} />

      {/* Public auth related pages */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/check-user-type" element={<CheckUserType />} />
      <Route path="/welcome" element={<Welcome />} />

      {/* ── Dashboard (persistent shell — sidebar never re-mounts) ── */}
      <Route path="/dashboard" element={<DashboardShell />}>
        <Route index                               element={<DashboardHome />} />
        <Route path="ai"                           element={<Dashboard />} />
        <Route path="ai/message/:slug"             element={<Dashboard />} />
        <Route path="email"                        element={<DashboardEmail />} />
        <Route path="workflows"                    element={<DashboardWorkflows />} />
        <Route path="workflows/new"                element={<DashboardTaskCreate />} />
        <Route path="workflows/task/:code"         element={<DashboardTaskDetail />} />
        <Route path="activity"                     element={<DashboardActivity />} />
        <Route path="notifications"                element={<DashboardNotifications />} />
        <Route path="apply-leave"                  element={<ApplyLeave />} />
        <Route path="holidays"                     element={<Holidays />} />
        <Route path="salary-slips"                 element={<SalarySlips />} />
        <Route path="hr/leave-approval"           element={<LeaveApproval />} />
        <Route path="hr/jobs"                     element={<DashboardJobs />} />
        <Route path="hr/job-applications"         element={<DashboardJobApplications />} />
        <Route path="hr/salary-slips"             element={<DashboardPayroll />} />
        <Route path="seo/tasks"                   element={<DashboardSeoTasks />} />
        <Route path="my-profile"                  element={<MyProfile />} />
        <Route path="account/transactions"         element={<AccountTransactions />} />
        <Route path="super-admin/services"            element={<DashboardServices />} />
        <Route path="super-admin/services/:id"      element={<DashboardServiceDetail />} />
        <Route path="super-admin/clients"          element={<DashboardClients />} />
        <Route path="super-admin/projects"            element={<DashboardProjects />} />
        <Route path="super-admin/projects/:id"      element={<DashboardProjectDetail />} />
        <Route path="super-admin/employee-management" element={<DashboardEmployeeManagement />} />
        <Route path="super-admin/employee-management/:employee_id" element={<DashboardEmployeeDetail />} />

        {/* ── Placeholder pages (modules under development — avoid 404) ── */}

        {/* Website Admin */}
        <Route path="website-admin"        element={<ComingSoon section="Website Admin" title="Website Overview" icon="bi-speedometer2"   description="Monitor website performance, traffic, and content at a glance." features={["Live traffic analytics","Content health summary","Quick publish shortcuts"]} />} />
        <Route path="website-admin/pages"  element={<ComingSoon section="Website Admin" title="Pages"            icon="bi-file-text-fill"  description="Create, edit, and manage all website pages." features={["Page builder","SEO meta management","Draft & publish workflow"]} />} />
        <Route path="website-admin/media"  element={<ComingSoon section="Website Admin" title="Media Library"    icon="bi-images"          description="Upload and organize images, videos, and documents." features={["Drag & drop uploads","Folder organization","Image optimization"]} />} />

        {/* HR */}
        <Route path="hr"             element={<ComingSoon section="HR" title="HR Overview"  icon="bi-person-lines-fill"   description="Human resources dashboard with team insights." features={["Headcount summary","Pending approvals","Upcoming events"]} />} />
        <Route path="hr/attendance"  element={<ComingSoon section="HR" title="Attendance"   icon="bi-clock-fill"          description="Track daily attendance and working hours." features={["Daily check-in/out","Monthly attendance report","Late & absence tracking"]} />} />
        <Route path="hr/leaves"      element={<ComingSoon section="HR" title="Leaves"       icon="bi-calendar-check-fill" description="Manage and review team leave records." features={["Leave balances","Calendar view","Leave history"]} />} />

        {/* Accounts */}
        <Route path="account"          element={<ComingSoon section="Accounts" title="Accounts Overview" icon="bi-pie-chart-fill"    description="Financial overview with income, expenses, and balances." features={["Revenue & expense summary","Cash flow charts","Outstanding balances"]} />} />
        <Route path="account/invoices" element={<ComingSoon section="Accounts" title="Invoices"          icon="bi-receipt"           description="Generate, send, and track client invoices." features={["Invoice generation","Payment status tracking","Auto reminders"]} />} />
        <Route path="account/payments" element={<AccountPayments />} />
        <Route path="account/expenses" element={<AccountPayments />} />

        {/* Admin */}
        <Route path="admin"          element={<ComingSoon section="Admin" title="Admin Overview" icon="bi-layout-text-sidebar-reverse"  description="Administrative control center for the organization." features={["System overview","Activity logs","Quick controls"]} />} />
        <Route path="admin/reports"  element={<ComingSoon section="Admin" title="Reports"        icon="bi-file-earmark-bar-graph-fill"  description="Generate and export detailed business reports." features={["Custom report builder","Export to PDF/Excel","Scheduled reports"]} />} />
        <Route path="admin/controls" element={<ComingSoon section="Admin" title="Controls"       icon="bi-toggles"                      description="Configure system-wide settings and toggles." features={["Feature flags","Access policies","System preferences"]} />} />

        {/* Team */}
        <Route path="employee"         element={<ComingSoon section="Team" title="Team Overview" icon="bi-person-fill"     description="Your personal workspace overview." features={["My tasks summary","Recent activity","Quick links"]} />} />
        <Route path="employee/profile" element={<ComingSoon section="Team" title="Profile"       icon="bi-person-circle"   description="View and manage your team profile." features={["Profile details","Skills & roles","Preferences"]} />} />
        <Route path="employee/tasks"   element={<ComingSoon section="Team" title="My Tasks"      icon="bi-check2-square"   description="All tasks assigned to you in one place." features={["Task list","Status updates","Due-date reminders"]} />} />

        {/* SEO */}
        <Route path="seo"          element={<ComingSoon section="SEO" title="SEO Overview" icon="bi-bar-chart-line-fill"  description="Search engine optimization performance dashboard." features={["Keyword rankings","Traffic insights","Site health score"]} />} />
        <Route path="seo/projects" element={<ComingSoon section="SEO" title="SEO Projects" icon="bi-search"               description="Manage SEO campaigns and client projects." features={["Project tracking","Keyword targets","Progress reports"]} />} />
        <Route path="seo/reports"  element={<ComingSoon section="SEO" title="SEO Reports"  icon="bi-file-earmark-bar-graph" description="Generate and share SEO performance reports." features={["Ranking reports","Backlink analysis","Client-ready exports"]} />} />

        {/* Header dropdown */}
        <Route path="settings" element={<ComingSoon section="Account" title="Settings"       icon="bi-gear-fill"          description="Manage your account preferences and settings." features={["Notification preferences","Theme & display","Security settings"]} />} />
        <Route path="help"     element={<ComingSoon section="Support" title="Help & Support" icon="bi-question-circle-fill" description="Get help, browse guides, and contact support." features={["Help articles","Video tutorials","Contact support"]} />} />
      </Route>

      {/* Job pages */}
      <Route path="/job-details/:slug" element={<JobDetails />} />
      <Route path="/apply-job/:slug" element={<ApplyJob />} />
    </Routes>
  );

  // Dashboard: no transition — persistent shell must not re-mount
  if (isDashboard) {
    return <div className="content">{routes}</div>;
  }

  // Public pages: quick fade-out → scroll top (masked by fade) → fade-in new page
  // The .content wrapper itself is permanent (never unmounts) so it keeps
  // occupying its flex space in the page shell through the exit/enter gap —
  // only the animated page inside it swaps. Without this, AnimatePresence's
  // mode="wait" briefly removes .content from the DOM entirely between
  // pages, leaving the footer as the only flex child for a frame.
  return (
    <div className="content">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeInOut" }}
        >
          {routes}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// ✅ New inner app shell so we can use useLocation safely
const AppLayout = ({ loadingComplete, handleLoaderComplete }) => {
  const location = useLocation();

  // ✅ List only those routes where header/footer should be hidden
  // Login and Welcome are intentionally NOT included here
  const dashboardHiddenLayoutRoutes = [
    "/dashboard",
    // add more exact routes here later if needed
    // "/dashboard/profile",
    // "/dashboard/settings",
  ];

  // ✅ Hide layout for exact dashboard route OR any nested dashboard path
  const hideHeaderFooter =
    dashboardHiddenLayoutRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/dashboard/");

  return (
    <Suspense fallback={null}>
      {/* Loader overlay (Router stays mounted) */}
      {!loadingComplete && <PageLoader onComplete={handleLoaderComplete} />}

      <ScrollToTop />

      {/* ✅ Show navbar only when page is NOT part of dashboard area */}
      {!hideHeaderFooter && <Navigation />}

      {/* ✅ Track page views only after loader completes */}
      <RouteTracker enabled={loadingComplete} />

      {/* ✅ Mount pages only when loader is done */}
      <ContentWrapper enabled={loadingComplete} />

      {/* ✅ Cookie banner should appear only after loader completes */}
      {loadingComplete && <CookieBanner />}

      {/* ✅ Show footer only when page is NOT part of dashboard area, and only
          once real content has mounted — otherwise the footer (often taller
          than one viewport once its columns stack on mobile) is the only
          thing in the flex flow and renders pinned to the top instead of
          off-screen at the bottom. */}
      {!hideHeaderFooter && loadingComplete && <Footer />}
    </Suspense>
  );
};

// 🔧 DEV TOGGLE — set to true to show the loader on EVERY reload while developing.
//    Keep false for production (loader shows once per browser session).
const ALWAYS_SHOW_LOADER = false;

const App = () => {
  const [loadingComplete, setLoadingComplete] = useState(() =>
    ALWAYS_SHOW_LOADER ? false : Boolean(sessionStorage.getItem("pageLoaded"))
  );

  // Disable browser scroll restoration so it never fights our manual scroll-to-top
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: "ease-in-out",
      offset: 50,
    });
  }, []);

  // IMPORTANT: When loader finishes and app mounts, refresh AOS so elements animate correctly
  useEffect(() => {
    if (loadingComplete) {
      const t = setTimeout(() => {
        AOS.refreshHard();
      }, 50);
      return () => clearTimeout(t);
    }
  }, [loadingComplete]);

  const handleLoaderComplete = () => {
    sessionStorage.setItem("pageLoaded", "true");
    // Scroll before unmounting loader so the page is already at top when it appears
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setLoadingComplete(true);
    // Belt-and-suspenders: repeat after paint in case mobile layout shift moves scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      });
    });
    // Final fallback for slow PWA paint cycles
    setTimeout(() => window.scrollTo({ top: 0, left: 0, behavior: "instant" }), 100);
  };

  return (
    <Router>
      <AppLayout
        loadingComplete={loadingComplete}
        handleLoaderComplete={handleLoaderComplete}
      />
    </Router>
  );
};

export default App;